import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PersonIcon from '@mui/icons-material/Person';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import SchoolIcon from '@mui/icons-material/School';
import logger from '../utils/logger';
import BusinessIcon from '@mui/icons-material/Business';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import DescriptionIcon from '@mui/icons-material/Description';
import TaskUploader from '../components/Common/TaskUploader';
import LoginModal from '../components/Common/LoginModal';
import { uploadFiles } from '../store/actions/tasksActions';

// "不再提示" 持久化 key
const FILE_TYPE_REMINDER_DISMISSED_KEY = 'pagesmooth.createTask.fileTypeReminderDismissed';

// 文档类型配置 - 根据Landing页面支持的文档类型
const DOCUMENT_TYPES = [
  {
    id: 'personal',
    name: '个人证件',
    description: '身份证、驾驶证等个人证件翻译认证',
    icon: <PersonIcon sx={{ fontSize: 48 }} />,
    color: '#4353FF',
    docs: ['身份证', '出生证明', '工作证', '驾驶证', '行驶证', '车辆登记证书', '退休证'],
    dropzoneTitle: '拖放个人证件到这里或点击上传',
    dropzoneDescription: '支持JPG、PNG、PDF格式，单个文件最大50M，每个证件需要单独一个文件上传'
  },
  {
    id: 'family',
    name: '家庭证件',
    description: '结婚证、离婚证、户口本等家庭证件翻译',
    icon: <FamilyRestroomIcon sx={{ fontSize: 48 }} />,
    color: '#EC4899',
    docs: ['结婚证', '离婚证', '户口本', '房产证'],
    dropzoneTitle: '拖放家庭证件到这里或点击上传',
    dropzoneDescription: '支持JPG、PNG、PDF格式，单个文件最大50M，每个证件需要单独一个文件上传'
  },
  {
    id: 'education',
    name: '学历证书',
    description: '毕业证、学位证、成绩单等学历相关翻译',
    icon: <SchoolIcon sx={{ fontSize: 48 }} />,
    color: '#8B5CF6',
    docs: [
      '毕业证',
      '学位证',
      { name: '成绩单', disabled: true, reason: '维护中，暂不支持翻译' },
      '学生证',
      '教师资格证'
    ],
    dropzoneTitle: '拖放学历证书到这里或点击上传',
    dropzoneDescription: '支持JPG、PNG、PDF格式，单个文件最大50M，每个证件需要单独一个文件上传'
  },
  {
    id: 'business',
    name: '企业证件',
    description: '营业执照、商标证书等企业文件翻译',
    icon: <BusinessIcon sx={{ fontSize: 48 }} />,
    color: '#10B981',
    docs: ['营业执照', '税务登记证', '商标证书', '开户许可证'],
    dropzoneTitle: '拖放企业证件到这里或点击上传',
    dropzoneDescription: '支持JPG、PNG、PDF格式，单个文件最大50M，每个证件需要单独一个文件上传'
  },
  {
    id: 'financial',
    name: '流水清单',
    description: '银行流水账单、纳税清单等财务文件翻译',
    icon: <ReceiptLongIcon sx={{ fontSize: 48 }} />,
    color: '#F59E0B',
    docs: ['银行流水账单', '个人所得税纳税清单'],
    dropzoneTitle: '拖放流水清单到这里或点击上传',
    dropzoneDescription: '支持JPG、PNG、PDF格式，单个文件最大50M，每个证件需要单独一个文件上传'
  },
  {
    id: 'other',
    name: '其它证明',
    description: '在职证明、离职证明等其他证明文件翻译',
    icon: <DescriptionIcon sx={{ fontSize: 48 }} />,
    color: '#6B78FF',
    docs: ['在读证明', '实习证明', '在职证明', '工作证明', '离职证明'],
    dropzoneTitle: '拖放证明类文件到这里或点击上传',
    dropzoneDescription: '支持JPG、PNG、PDF格式，单个文件最大50M，每个证件需要单独一个文件上传'
  }
];


const CreateTask = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState(null);
  // 文件类型提醒弹窗
  const [showFileTypeReminder, setShowFileTypeReminder] = useState(false);
  const [reminderDontShowAgain, setReminderDontShowAgain] = useState(false);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // 处理文档类型卡片点击
  const handleDocTypeClick = (docType) => {
    setSelectedDocType(docType);
    // 若用户曾勾选"不再提示"，直接进入上传弹窗；否则先弹提醒
    const dismissed = localStorage.getItem(FILE_TYPE_REMINDER_DISMISSED_KEY) === 'true';
    if (dismissed) {
      setUploadDialogOpen(true);
    } else {
      setShowFileTypeReminder(true);
    }
  };

  // 关闭上传弹窗
  const handleCloseUploadDialog = () => {
    setUploadDialogOpen(false);
    setSelectedDocType(null);
  };

  // 提醒弹窗：用户点击"我已知晓，继续上传"
  const handleReminderConfirm = () => {
    if (reminderDontShowAgain) {
      localStorage.setItem(FILE_TYPE_REMINDER_DISMISSED_KEY, 'true');
    }
    setShowFileTypeReminder(false);
    setReminderDontShowAgain(false);
    setUploadDialogOpen(true);
  };

  // 提醒弹窗：用户关闭（右上角 X / 点遮罩 / 点击"取消"）
  const handleReminderClose = () => {
    setShowFileTypeReminder(false);
    setReminderDontShowAgain(false);
    setSelectedDocType(null);
  };

  // 处理文件上传完成
  const handleUploadComplete = async (files, language, certification_type = 'none', is_urgent = false) => {
    // 如果用户未登录，显示登录弹窗
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return Promise.reject(new Error('用户未登录'));
    }

    try {
      // 调用上传文件API
      const response = await dispatch(uploadFiles(files, language, certification_type, is_urgent));

      // 关闭上传弹窗
      handleCloseUploadDialog();

      // 上传成功后跳转工作台
      navigate('/workspace');

      return response;
    } catch (error) {
      logger.error('上传失败:', error);

      // 错误时不关闭弹窗，让用户可以修改后重试
      // 只有任务成功创建才关闭弹窗并跳转
      return Promise.reject(error);
    }
  };

  // 处理登录成功
  const handleLoginSuccess = () => {
    setShowLoginModal(false);
  };

  return (
    <Box sx={{ height: '100%', position: 'relative' }}>
      {/* 页面标题 */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 600,
            background: 'linear-gradient(90deg, #4353FF 0%, #4353FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            marginLeft: '10px',
            mb: 1
          }}
        >
          创建任务
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ marginLeft: '10px' }}
        >
请选择平台支持的文件类型提交翻译任务；若因上传非支持格式文件造成翻译失败，相关责任需由您自行承担。
        </Typography>
      </Box>

      {/* 文档类型卡片矩阵 */}
      <Grid container spacing={3}>
        {DOCUMENT_TYPES.map((docType) => (
          <Grid item xs={12} sm={6} md={4} key={docType.id}>
            <Card
              sx={{
                height: '100%',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: `2px solid transparent`,
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.15)',
                  border: `2px solid ${docType.color}`
                }
              }}
            >
              <CardActionArea
                onClick={() => handleDocTypeClick(docType)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  p: 1
                }}
              >
                <CardContent sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%',
                  py: 3
                }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: docType.color + '15',
                      color: docType.color,
                      mb: 2
                    }}
                  >
                    {docType.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    component="h3"
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 1
                    }}
                  >
                    {docType.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: 'center', mb: 2 }}
                  >
                    {docType.description}
                  </Typography>
                  {/* 支持的文档类型标签 */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center' }}>
                    {docType.docs.map((doc, index) => {
                      const docName = typeof doc === 'string' ? doc : doc.name;
                      const isDisabled = typeof doc === 'object' && doc.disabled;
                      return (
                        <Box
                          key={index}
                          sx={{
                            px: 2,
                            py: 0.75,
                            backgroundColor: isDisabled ? '#9e9e9e15' : docType.color + '10',
                            borderRadius: 1,
                            fontSize: '0.875rem',
                            color: isDisabled ? '#9e9e9e' : docType.color,
                            fontWeight: 500,
                            position: 'relative',
                            ...(isDisabled && {
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                left: '10%',
                                top: '50%',
                                width: '80%',
                                height: '2px',
                                backgroundColor: '#9e9e9e',
                                transform: 'rotate(-5deg)'
                              }
                            })
                          }}
                          title={isDisabled ? doc.reason : ''}
                        >
                          {docName}
                        </Box>
                      );
                    })}
                  </Box>
                  {/* 学历证书维护提示 */}
                  {docType.id === 'education' && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#F59E0B',
                        mt: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      ⚠️ 成绩单等类型在维护中，请稍等
                    </Typography>
                  )}
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 底部备注 */}
      <Box sx={{ position: 'absolute', bottom: 16, left: 16 }}>
        <Typography variant="body2" sx={{ color: theme.palette.primary.main }}>
          特别说明：对于还不支持的文件类型，如有需求请联系客服
        </Typography>
      </Box>

      {/* 文件类型提醒弹窗 - 每次点卡片时弹出，可选"不再提示" */}
      <Dialog
        open={showFileTypeReminder}
        onClose={handleReminderClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        {/* 标题栏 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            backgroundColor: '#FFF7E6',
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: '#D97706', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400E' }}>
              上传前请仔细阅读
            </Typography>
          </Box>
          <IconButton onClick={handleReminderClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 内容 */}
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'text.primary' }}>
            请<Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>详细查阅</Box>
            下方平台当前支持的全部文件类型后再上传。
            <Box component="span" sx={{ fontWeight: 700 }}>不支持的文件类型将无法上传，或可能导致翻译失败。</Box>
          </Typography>

          <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, color: 'text.secondary' }}>
            提交任务前，请认真阅读每一类文件对应的规范要求。
            <Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>因上传非支持格式、上传非支持文件，或未按规范提交所造成的翻译失败、无法交付等问题，</Box>
            <Box component="span" sx={{ fontWeight: 700, color: 'error.main' }}>所有责任由用户本人承担</Box>
            ，平台不承担因此产生的任何损失。
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* 支持的文件类型总览 */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            平台当前支持的文件类型：
          </Typography>
          <Box
            sx={{
              backgroundColor: theme.palette.grey[50],
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              maxHeight: 260,
              overflow: 'auto'
            }}
          >
            {DOCUMENT_TYPES.map((docType) => (
              <Box key={docType.id} sx={{ mb: 1.5, '&:last-child': { mb: 0 } }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: docType.color, mb: 0.5 }}>
                  {docType.name}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {docType.docs.map((doc, idx) => {
                    const docName = typeof doc === 'string' ? doc : doc.name;
                    const isDisabled = typeof doc === 'object' && doc.disabled;
                    return (
                      <Box
                        key={idx}
                        sx={{
                          px: 1,
                          py: 0.25,
                          backgroundColor: isDisabled ? '#9e9e9e15' : docType.color + '12',
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          color: isDisabled ? '#9e9e9e' : docType.color,
                          fontWeight: 500,
                          textDecoration: isDisabled ? 'line-through' : 'none'
                        }}
                      >
                        {docName}{isDisabled ? '（暂不支持）' : ''}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>

          {/* 不再提示 */}
          <FormControlLabel
            control={
              <Checkbox
                checked={reminderDontShowAgain}
                onChange={(e) => setReminderDontShowAgain(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                我已知晓上述说明，不再弹出此提醒
              </Typography>
            }
            sx={{ mt: 2, mb: 0 }}
          />
        </DialogContent>

        {/* 操作按钮 */}
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1 }}>
          <Button
            onClick={handleReminderClose}
            sx={{
              textTransform: 'none',
              px: 3,
              color: 'text.secondary',
              borderColor: theme.palette.divider
            }}
            variant="outlined"
          >
            取消
          </Button>
          <Button
            onClick={handleReminderConfirm}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              fontWeight: 600
            }}
          >
            我已知晓，继续上传
          </Button>
        </DialogActions>
      </Dialog>

      {/* 上传弹窗 */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleCloseUploadDialog}
        fullScreen={fullScreen}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          elevation: 3,
          sx: {
            borderRadius: 3,
            height: '85vh',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {/* 弹窗标题栏 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
            backgroundColor: selectedDocType ? selectedDocType.color + '10' : theme.palette.primary.main + '10',
            flexShrink: 0
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedDocType && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: selectedDocType.color + '20',
                  color: selectedDocType.color
                }}
              >
                {React.cloneElement(selectedDocType.icon, { sx: { fontSize: 24 } })}
              </Box>
            )}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {selectedDocType ? selectedDocType.name : '上传文件'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedDocType ? `支持: ${selectedDocType.docs.join('、')}` : '请上传您的证件文件'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseUploadDialog}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 弹窗内容 */}
        <DialogContent
          sx={{
            p: 0,
            overflow: 'auto',
            flex: 1,
            minHeight: 0
          }}
        >
          <TaskUploader
            onComplete={handleUploadComplete}
            isDialog={true}
            onClose={handleCloseUploadDialog}
            requireAuth={false}
            dropzoneTitle={selectedDocType?.dropzoneTitle || '拖放文件到这里或点击上传'}
            dropzoneDescription={selectedDocType?.dropzoneDescription || '支持JPG、PNG、PDF格式，单个文件最大50MB'}
          />
        </DialogContent>
      </Dialog>

      {/* 登录弹窗 */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Box>
  );
};

export default CreateTask;